import { useEffect, useState, useMemo } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import Header from "@/components/Header";
import CollectibleCard from "@/components/CollectibleCard";
import { Sparkles, Tag, ArrowRight } from "lucide-react";

export default function Gallery() {
  const [collectibles, setCollectibles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([api.get("/collectibles"), api.get("/categories")])
      .then(([cRes, catRes]) => {
        setCollectibles(cRes.data);
        setCategories(catRes.data);
      })
      .catch(() => {
        setCollectibles([]);
        setCategories([]);
      })
      .finally(() => setLoading(false));
  }, []);

  // Build ordered sections: each managed category (with items) then Uncategorized.
  const sections = useMemo(() => {
    const catIds = new Set(categories.map((c) => c.id));
    const result = categories
      .map((cat) => ({
        id: cat.id,
        name: cat.name,
        items: collectibles.filter((c) => c.category_id === cat.id),
      }))
      .filter((s) => s.items.length > 0);
    const uncategorized = collectibles.filter((c) => !c.category_id || !catIds.has(c.category_id));
    if (uncategorized.length > 0) {
      result.push({ id: "__uncategorized__", name: "Uncategorized", items: uncategorized });
    }
    return result;
  }, [collectibles, categories]);

  return (
    <div className="relative min-h-screen">
      <div className="glow-bg" />
      <Header />

      <main className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10 pt-32 pb-24">
        {/* Hero */}
        <section className="mb-20 md:mb-28">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="flex items-center gap-2 mb-6">
              <span className="w-8 h-px bg-glow-primary" />
              <span className="font-mono text-xs uppercase tracking-[0.3em] text-glow-primary">
                Digital Collectibles Vault
              </span>
            </div>
            <h1 className="font-heading font-900 text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.95] text-white max-w-4xl">
              G.L.O.W
              <span className="block text-glow-primary drop-shadow-[0_0_24px_rgba(0,240,255,0.5)]">
                GALLERY
              </span>
            </h1>
            <p className="mt-8 font-mono text-sm md:text-base text-white/60 leading-relaxed max-w-2xl">
              A curated exhibition of rare digital artifacts, organized by category.
              Each collectible is archived with up to six high-fidelity captures and a
              full dossier. Browse the vault below.
            </p>
          </motion.div>
        </section>

        {/* Sections */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="min-h-[280px] bg-surface border border-white/10 animate-pulse" />
            ))}
          </div>
        ) : collectibles.length === 0 ? (
          <div className="border border-dashed border-white/15 py-32 text-center" data-testid="empty-gallery">
            <p className="font-heading font-500 text-xl text-white/70 uppercase tracking-widest">Vault Empty</p>
            <p className="font-mono text-sm text-white/40 mt-3">No collectibles have been archived yet.</p>
          </div>
        ) : (
          <div className="space-y-20 md:space-y-28" data-testid="gallery-sections">
            {sections.map((section) => (
              <section key={section.id} data-testid={`category-section-${section.id}`}>
                <div className="flex items-center justify-between mb-10 border-b border-white/10 pb-4">
                  <h2 className="font-heading font-500 text-lg uppercase tracking-widest text-white flex items-center gap-3">
                    {section.id === "__uncategorized__" ? (
                      <Sparkles className="w-5 h-5 text-glow-primary" />
                    ) : (
                      <Tag className="w-5 h-5 text-glow-accent" />
                    )}
                    {section.name}
                  </h2>
                  <span className="font-mono text-xs text-white/40" data-testid={`category-count-${section.id}`}>
                    {section.items.length} {section.items.length === 1 ? "ITEM" : "ITEMS"}
                  </span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 auto-rows-[280px] gap-6 md:gap-8">
                  {section.items.map((c, i) => (
                    <CollectibleCard key={c.id} collectible={c} index={i} />
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}

        {/* Community creators CTA */}
        <section className="mt-24 border-t border-white/10 pt-14" data-testid="community-cta">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6 bg-surface border border-white/10 p-8 md:p-10">
            <div>
              <span className="font-mono text-xs uppercase tracking-[0.3em] text-glow-accent">Community</span>
              <h3 className="font-heading font-700 text-2xl md:text-3xl tracking-tight text-white mt-3">
                Explore Creator Vaults
              </h3>
              <p className="font-mono text-sm text-white/50 mt-3 max-w-xl">
                Browse personal galleries curated by G.L.O.W creators.
              </p>
            </div>
            <Link to="/creators" className="shrink-0">
              <span
                className="inline-flex items-center gap-2 bg-glow-accent text-black font-bold font-mono text-xs uppercase tracking-wider px-6 py-4 hover:bg-glow-accent/80 transition-colors"
                data-testid="browse-creators-btn"
              >
                Browse Creators <ArrowRight className="w-4 h-4" />
              </span>
            </Link>
          </div>
        </section>
      </main>
    </div>
  );
}
